import { useState, useCallback } from "react";

interface MultiSelectConfig<T, K = string> {
    getItemKey: (item: T) => K;
    getItemGroup?: (item: T) => string;
    groupedItems?: Record<string, T[]>;
}

export const useMultiSelect = <T, K = string>(config: MultiSelectConfig<T, K>) => {
    const [selectedKeys, setSelectedKeys] = useState<K[]>([]);

    const handleItemClick = useCallback((item: T, event: React.MouseEvent) => {
        const itemKey = config.getItemKey(item);

        setSelectedKeys((prev) => {
            if (event.ctrlKey || event.metaKey) {
                // Toggle selection
                return prev.includes(itemKey)
                    ? prev.filter((key) => key !== itemKey)
                    : [...prev, itemKey];
            } else if (event.shiftKey && prev.length > 0 && config.getItemGroup && config.groupedItems) {
                // Range select within the same group
                const itemGroup = config.getItemGroup(item);
                const group = config.groupedItems[itemGroup];
                if (!group) return prev;

                const lastSelectedKey = prev[prev.length - 1];
                const lastSelectedIndex = group.findIndex(g => config.getItemKey(g) === lastSelectedKey);
                const clickedIndex = group.findIndex(g => config.getItemKey(g) === itemKey);

                if (lastSelectedIndex === -1 || clickedIndex === -1) return prev;

                const [start, end] = [lastSelectedIndex, clickedIndex].sort((a, b) => a - b);
                const rangeKeys = group.slice(start, end + 1).map(g => config.getItemKey(g));
                return Array.from(new Set([...prev, ...rangeKeys]));
            } else {
                // Single select
                return [itemKey];
            }
        });
    }, [config]);

    const clearSelection = useCallback(() => {
        setSelectedKeys([]);
    }, []);

    const selectItems = useCallback((keys: K[]) => {
        setSelectedKeys(keys);
    }, []);

    const isSelected = useCallback((item: T) => {
        return selectedKeys.includes(config.getItemKey(item));
    }, [selectedKeys, config]);

    return {
        selectedKeys,
        handleItemClick,
        clearSelection,
        selectItems,
        isSelected,
    };
};
